import { useMemo } from 'react';
import { css } from '@emotion/css';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import checkIconTokens from '@amzn/meridian-tokens/base/icon/check-small';
import copyIconTokens from '@amzn/meridian-tokens/base/icon/copy';
import downloadIconTokens from '@amzn/meridian-tokens/base/icon/download-small';
import useCopyToClipboard, { describeCopyState } from '../hooks/useCopyToClipboard';
import ActionButton from './ActionButton';
import fontStacks from '../theme/font-stacks';

interface SchemaViewerProps {
  code: string;
  title?: string;
  fileName?: string;
}

const codeBlockClass = css({
  fontFamily: fontStacks.monospace,
  fontSize: 13,
  lineHeight: 1.5,
  margin: 0,
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
  width: '100%',
  wordBreak: 'break-word',
});

function SchemaViewer({ code, title, fileName = 'input-schema.json' }: SchemaViewerProps) {
  const { copyState, copy } = useCopyToClipboard();
  const lineCount = useMemo(() => code.split('\n').length, [code]);

  const handleCopy = () => copy(code);

  const handleDownload = () => {
    const blob = new Blob([code], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Column spacing="none" spacingInset="none" width="100%">
      <Row
        spacing="200"
        spacingInset="300"
        alignmentVertical="center"
        alignmentHorizontal="justify"
        widths={['fill', 'fit']}
        wrap="down"
      >
        <Column spacing="100">
          {title && <Text type="h100">{title}</Text>}
          <Text type="b100" color="secondary">
            {lineCount.toLocaleString()} lines
          </Text>
        </Column>
        <Row spacing="200">
          <ActionButton
            variant="secondary"
            size="small"
            iconTokens={copyState === 'copied' ? checkIconTokens : copyIconTokens}
            onClick={handleCopy}
          >
            {describeCopyState(copyState)}
          </ActionButton>
          <ActionButton variant="secondary" size="small" iconTokens={downloadIconTokens} onClick={handleDownload}>
            Download
          </ActionButton>
        </Row>
      </Row>
      <Box spacingInset="400" overflowX="hidden" backgroundColor="subdued">
        <pre className={codeBlockClass}>
          <code>{code}</code>
        </pre>
      </Box>
    </Column>
  );
}

export default SchemaViewer;
